import { useState, useMemo, useCallback } from 'react';
import {
  SMTDailyLogEntriesAccordion,
  SMTDailyLogEntriesAccordionProps,
  SMTFullImageModal,
  SMTEntry as SMTComponentEntry,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import { transformSmtEntry } from '~/components/maud/patients/profile/smt/dailyLog.util';
import AnalyticsEvents from '~/analytics';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface SMTDailyLogWrapperProps
  extends Omit<SMTDailyLogEntriesAccordionProps, 'entries'> {
  dailyLog: DailyLog;
  patientId: string;
  onAddComment: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  onEdit: (entryId: string, entry?: SMTEntry) => void;
}

const SMTDailyLogWrapper = ({
  dailyLog,
  onAddComment,
  onDelete,
  onEdit,
  patientId,
  ...accordionProps
}: SMTDailyLogWrapperProps) => {
  const { trackEvent } = useAnalytics();

  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    imageUrl?: string;
    fileName?: string;
  }>({ isOpen: false });

  const handleImageClick = useCallback(
    (url: string, fileName: string) => {
      trackEvent(AnalyticsEvents.imageClicked, {
        patientId,
      });
      setImageModal({
        fileName,
        imageUrl: url,
        isOpen: true,
      });
    },
    [trackEvent, patientId],
  );

  const closeModal = () => {
    trackEvent(AnalyticsEvents.imageModalClosed, {
      patientId,
    });
    setImageModal({ isOpen: false });
  };

  const handleMenuOpen = useCallback(
    (entryId: string) => {
      trackEvent(AnalyticsEvents.entryKebabMenuClicked, {
        entryId,
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    },
    [trackEvent, patientId],
  );

  const handleZoomIn = () => {
    trackEvent(AnalyticsEvents.imageZoomIn, {
      patientId,
    });
  };

  const handleZoomOut = () => {
    trackEvent(AnalyticsEvents.imageZoomOut, {
      patientId,
    });
  };

  const handleZoomReset = () => {
    trackEvent(AnalyticsEvents.imageZoomReset, {
      patientId,
    });
  };

  // Transform raw daily log data to entries with image components and kebab menu options
  const transformedEntries: SMTComponentEntry[] = useMemo(() => {
    if (isEmpty(dailyLog.smtEntries)) {
      return [];
    }

    return dailyLog.smtEntries.map((entry) =>
      transformSmtEntry({
        entry,
        handleImageClick,
        handlers: {
          onAddComment,
          onDelete,
          onEdit,
        },
        onMenuOpen: handleMenuOpen,
      }),
    );
  }, [
    dailyLog.smtEntries,
    onAddComment,
    onDelete,
    onEdit,
    handleImageClick,
    handleMenuOpen,
  ]);

  return (
    <>
      <SMTDailyLogEntriesAccordion
        {...accordionProps}
        entries={transformedEntries}
      />
      <SMTFullImageModal
        alt={imageModal.fileName || ''}
        isOpen={imageModal.isOpen}
        onClose={closeModal}
        onResetZoom={handleZoomReset}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        src={imageModal.imageUrl}
      />
    </>
  );
};

export default SMTDailyLogWrapper;
